/**
 * Pure helpers for Kanban DnD index mapping.
 * Kept free of model imports so unit tests stay lightweight.
 */

export function isColumnVisible(column) {
    if (!column?.isSmart)
        return true;

    if (!column.rules?.hideIfEmpty)
        return true;

    return (column.changes?.length || 0) > 0;
}

export function changeId(change) {
    if (!change)
        return undefined;

    return typeof change === 'object' ? change._id : change;
}

/**
 * Change ids that appear in at least one smart column on the board.
 */
export function collectSmartChangeIds(columns) {
    const ids = new Set();
    (columns || []).forEach((column) => {
        if (!column?.isSmart)
            return;

        (column.changes || []).forEach((change) => {
            const id = changeId(change);
            if (id)
                ids.add(id);
        });
    });
    return ids;
}

/**
 * Display-only: hide cards from normal columns when they also appear in a smart
 * column. Smart columns are left unchanged (a card may show in several smarts).
 * Does not mutate the input columns array.
 */
export function withoutSmartOverlapsInNormalColumns(columns) {
    const smartIds = collectSmartChangeIds(columns);
    if (smartIds.size === 0)
        return columns;

    return (columns || []).map((column) => {
        if (column?.isSmart)
            return column;

        return {
            ...column,
            changes: (column.changes || []).filter(
                (change) => !smartIds.has(changeId(change))
            ),
        };
    });
}

/**
 * Insert a column before the first smart column (or append if none).
 * Keeps manual workflow columns to the left of status/smart columns.
 */
export function insertColumnBeforeFirstSmart(columns, column) {
    const list = [...(columns || [])];
    const firstSmart = list.findIndex((entry) => entry?.isSmart);
    if (firstSmart === -1)
        list.push(column);
    else
        list.splice(firstSmart, 0, column);
    return list;
}

/**
 * Reorder full columns list from a DnD result over the visible columns only.
 */
export function reorderColumnsFromVisibleDrag(
    columns,
    sourceIndex,
    destinationIndex,
    draggableId
) {
    const visible = columns.filter(isColumnVisible);
    const moved = columns.find((column) => column._id === draggableId) || visible[sourceIndex];
    if (!moved)
        return columns;

    const without = columns.filter((column) => column._id !== moved._id);
    const visibleWithout = without.filter(isColumnVisible);

    let insertAt = without.length;
    if (destinationIndex < visibleWithout.length) {
        const before = visibleWithout[destinationIndex];
        insertAt = without.findIndex((column) => column._id === before._id);
    } else if (visibleWithout.length > 0) {
        const last = visibleWithout[visibleWithout.length - 1];
        insertAt = without.findIndex((column) => column._id === last._id) + 1;
    }

    if (insertAt < 0)
        insertAt = without.length;

    without.splice(insertAt, 0, moved);
    return without;
}

/**
 * Move a card inside one column using DnD indices from a possibly filtered list.
 */
export function reorderChangesFromVisibleDrag(
    changes,
    draggableId,
    destinationIndex,
    isVisible
) {
    const full = [...changes];
    const from = full.findIndex((change) => changeId(change) === draggableId);
    if (from === -1)
        return changes;

    const [item] = full.splice(from, 1);
    const visibleIds = full.filter(isVisible).map(changeId);

    let insertAt = full.length;
    if (destinationIndex < visibleIds.length) {
        const beforeId = visibleIds[destinationIndex];
        insertAt = full.findIndex((change) => changeId(change) === beforeId);
    } else if (visibleIds.length > 0) {
        const lastId = visibleIds[visibleIds.length - 1];
        insertAt = full.findIndex((change) => changeId(change) === lastId) + 1;
    }

    if (insertAt < 0)
        insertAt = full.length;

    full.splice(insertAt, 0, item);
    return full;
}
